import { isEmpty, clamp } from "lodash";
import {COMMON_PASSWORDS, MAX_EMAIL_PASSWORD_COMMON_SUBSTRING, MESSAGE} from "../constants"

const regexPat = /(.)\1{4,}/;

// Pattern to check if string is not empty
const EMPTY_REGEX = /(.|\s)*\S(.|\s)*/;
// Pattern to check if string has at least 9 characters
const MINIMUM_PASSWORD_LENGTH_REGEX = /^.{9,}$/;
// Pattern to check if string has at least one upper case letter
const UPPER_CASE_CHAR_REGEX = /(.*[A-Z].*)/;
// Pattern to check if string has at least one lower case letter
const LOWER_CASE_CHAR_REGEX = /(.*[a-z].*)/;
// Pattern to check if string has at least one number
const EXIST_NUMBER_REGEX = /(.*\d.*)/;
// Pattern to check if string has at least one non alpha numeric character (i.e. ?,!, %,$,  etc)
const EXIST_NON_ALPHA_NUMBER_REGEX = /(.*\W.*)/;
// Validators that will be used for scoring a given password
// Pattern to check is email is valid
const EMAIL_REGEX = /^[A-Z0-9._-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
const REGEX_PASSWORD_VALIDATORS = [
  EMPTY_REGEX,
  MINIMUM_PASSWORD_LENGTH_REGEX,
  LOWER_CASE_CHAR_REGEX,
  UPPER_CASE_CHAR_REGEX,
  EXIST_NUMBER_REGEX,
  EXIST_NON_ALPHA_NUMBER_REGEX
];
const PASSWORD_STRENGTH_RATING = ["","Poor", "Fair", "Good", "Excellent", "Excellent"];

const mapRegexToTrackerKey = (regex) => {
  if (regex === EMPTY_REGEX) {
    return "NOT_EMPTY";
  }
  if (regex === MINIMUM_PASSWORD_LENGTH_REGEX) {
    return "MIN_LENGTH";
  }
  if (regex === UPPER_CASE_CHAR_REGEX) {
    return "UPPER_CASE";
  }
  if (regex === LOWER_CASE_CHAR_REGEX) {
    return "LOWER_CASE";
  }
  if (regex === EXIST_NUMBER_REGEX) {
    return "NUMBER";
  }
  if (regex === EXIST_NON_ALPHA_NUMBER_REGEX) {
    return "NON_ALPHA";
  }
  return "";
}
/**
 * Gets password strength string based on score
 * Possible Strength Ratings Based on Score:
 *  - None(0), Poor(1), Fair(2), Good(3), Excellent(4)
 * @param {string} password
 * @returns {string}
 */

export const getPasswordStrength = (score) => {
  return PASSWORD_STRENGTH_RATING[score];
}

/**
 * Used for password validation for isPasswordValid3 (currently for B2C and password reset)
 * @param {*} password
 * @returns
 */

const isPasswordNotSimple2 = (password) => {
  if ((COMMON_PASSWORDS.indexOf(password) > -1) || regexPat.test(password)) { // weak password
    return false;
  }
  return true;
};

export const longestCommonPasswordSubstring = (string1, string2) => {
  return longestCommonSubstring(string1?.toLowerCase() ?? "", string2?.toLowerCase() ?? "") >= MAX_EMAIL_PASSWORD_COMMON_SUBSTRING
}

export const longestCommonSubstring = (string1, string2) => {
  const str1Len = string1?.length || "";
  const str2Len = string2?.length || "";
  const lengths = Array.from({ length: str1Len+1 }, () => Array(str2Len+1).fill(0));

  let max = 0
  for(let i = 1; i <= str1Len; i++) {
    for(let j = 1; j <= str2Len; j++) {
      if(string1[i-1] === string2[j-1]) {
        lengths[i][j] = lengths[i-1][j-1] + 1
        if(max < lengths[i][j]) {
          max = lengths[i][j];
        }
      }
    }
  }

  return max
}

export const getPasswordQualifications = (password) => {
  /**
   * qualifications:
   * Hard Stop: length >= 9 (MIN_LENGTH)
   * 3 of 4 must be met:
   * a) One Upper-Case letter (UPPER_CASE)
   * b) One Lower-Case letter (LOWER_CASE)
   * c) One Number (NUMBER)
   * d) One Non Alpha numeric [i.e. ?,!, %,$, etc] (NON_ALPHA)
   */

  const qualificationTracker = {
    "NOT_EMPTY": false,
    "MIN_LENGTH": false,
    "UPPER_CASE": false,
    "LOWER_CASE": false,
    "NUMBER": false,
    "NON_ALPHA": false,
  }

  REGEX_PASSWORD_VALIDATORS.forEach((curRegex) => {
    if (curRegex.test(password)) {
      qualificationTracker[mapRegexToTrackerKey(curRegex)] = true;
    }
  });

  return qualificationTracker;
}

/**
 * Latest password validation used for B2C registration and password reset page
 * Takes in a password and checks for multiple validations.
 * @param password
 * @returns {*}
 */


export const getFirstPasswordError = (email, password) => {
  const { newPassword } = isPasswordFieldValid(email)({
    currentPassword: "",
    newPassword: password
  });

  if(newPassword == null || newPassword.length === 0) {
    return null
  }

  return newPassword[0]
}

export const isPasswordFieldValid = (email) => (values) => {
  /**
   * qualifications:
   * Hard Stop: length >= 9 (MIN_LENGTH)
   * 3 of 4 must be met:
   * a) One Upper-Case letter (UPPER_CASE)
   * b) One Lower-Case letter (LOWER_CASE)
   * c) One Number (NUMBER)
   * d) One Non Alpha numeric [i.e. ?,!, %,$, etc] (NON_ALPHA)
   */
  // Excluding empty/password length validators as they do not contribute to the validation

  const EXCLUDED_SCORING_REGEX = [MINIMUM_PASSWORD_LENGTH_REGEX, EMPTY_REGEX];
  const errors = {};
  const newPassword = [];
  const passwordQualifications = getPasswordQualifications(values.newPassword);

  if (!passwordQualifications.NOT_EMPTY) {
    newPassword.push(MESSAGE.ERR_MANDATORY_PASSWORD);
  }

  if(longestCommonPasswordSubstring(values.newPassword, email)) {
    newPassword.push(MESSAGE.PASSWORD_CONTAINS_EMAIL);
  }
  if (!passwordQualifications.MIN_LENGTH) {
    newPassword.push(MESSAGE.REGISTER_PASSWORD_MIN_LENGTH);

  }
  if (values.newPassword === email && values.newPassword !== "") {
    newPassword.push(MESSAGE.REGISTER_PASSWORD_EMAIL_COPY_INVALID);
  }
  if (values.newPassword === values.currentPassword) {
    newPassword.push(MESSAGE.PASSWORD_USED);
  }

  const score = REGEX_PASSWORD_VALIDATORS.reduce((accumulator, curRegex) => (curRegex.test(values.newPassword) && !EXCLUDED_SCORING_REGEX.includes(curRegex)) ? (accumulator + 1) : accumulator, 0);

  if (score < 3) {
    newPassword.push(MESSAGE.REGISTER_PASSWORD_INVALID_REQ);
  }
  if (!isPasswordNotSimple2(values.newPassword)) {
    newPassword.push(MESSAGE.REGISTER_PASSWORD_COMMON_PASS);
  }

  if(!isEmpty(newPassword)) {
    errors.newPassword = newPassword;
  }

  return !isEmpty(errors) && errors;
};


/**
 *
 * Possible scores range between 0-4
 * * Edge Cases:
 *  - Strength = poor if it fails to reach minimum length
 * @param {string} password
 * @param {string} email
 * @returns
 */

export const getPasswordStrengthScore = (password, email) => {
  // Excluding empty string regex as it does not contribute to password strength
  const EXCLUDED_SCORING_REGEX = [EMPTY_REGEX];
  const score = REGEX_PASSWORD_VALIDATORS.reduce((accumulator, curRegex) => (
    curRegex.test(password) && !EXCLUDED_SCORING_REGEX.includes(curRegex)
  ) ? (accumulator + 1) : accumulator, 0);

  // Invalid scores will return default 0th index(empty string)
  if (score > (PASSWORD_STRENGTH_RATING.length - 1) || score < 0) {
    return 0;
  }

  // EDGE CASE: Score will be poor if length of password is not minimum length
  if (!MINIMUM_PASSWORD_LENGTH_REGEX.test(password) && score !== 0) {
    return 1;
  }

  if(longestCommonSubstring(password, email) >= 5) {
    return 1;
  }

  return clamp(score, 0, 4);
}

export const getEmailErrors =  (values, currentEmail) => {
  const errors = {
    email: []
  };

  if (isEmpty(values)) {
    errors.email.push(MESSAGE.EMAIL_MISSING);
  } else if (!EMAIL_REGEX.test(values)) {
    errors.email.push(MESSAGE.EMAIL_INVALID);
  } else if (values === currentEmail) {
    errors.email.push(MESSAGE.EMAIL_SAME_AS_CURRENT);
  } else {
    errors.email.pop();
  }

  return !isEmpty(errors.email) && errors;
}